// Libraries & Packages
import Color from "color";

// Utils
import CommonUtils from "components/UI/Utils/CommonUtils";

/**
 * ButtonUtils component: Styles
 */
class ButtonUtils {
  /**
   * Returns the Button component's color styles.
   *
   * @param {String} isHovered
   * @param {String} theme
   * @param {String} type
   * @param {String} variant
   * @returns {Object} An object containing the Button component's color styles.
   */
  static getButtonColorStyles(isHovered, theme, type, variant) {
    // Color
    const BUTTON_SOLID_DARKEN = 0.2;

    const BUTTON_OUTLINED_LIGHTEN = 0.2;
    const BUTTON_OUTLINED_DARKEN = 0.2;

    const BUTTON_LIGHT_DARKEN = 0.05;

    const buttonColorLookup = {
      //* Variant : Solid
      solid: {
        //* Type : Primary
        primary: isHovered
          ? {
              backgroundColor: "transparent",
              color: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
            }
          : {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastMain,
              borderColor: "transparent",
            },
        //* Type : Secondary
        secondary: isHovered
          ? {
              backgroundColor: Color(theme.palette.secondary.main)
                .darken(BUTTON_SOLID_DARKEN)
                .hex(),
              color: theme.palette.secondary.contrastMain,
              borderColor: "transparent",
            }
          : {
              backgroundColor: theme.palette.secondary.main,
              color: theme.palette.secondary.contrastMain,
              borderColor: "transparent",
            },
        //* Type : Success
        success: isHovered
          ? {
              backgroundColor: Color(theme.palette.success.main)
                .darken(BUTTON_SOLID_DARKEN)
                .hex(),
              color: theme.palette.success.contrastMain,
              borderColor: "transparent",
            }
          : {
              backgroundColor: theme.palette.success.main,
              color: theme.palette.success.contrastMain,
              borderColor: "transparent",
            },
        //* Type : Warning
        warning: isHovered
          ? {
              backgroundColor: Color(theme.palette.warning.main)
                .darken(BUTTON_SOLID_DARKEN)
                .hex(),
              color: theme.palette.warning.contrastMain,
              borderColor: "transparent",
            }
          : {
              backgroundColor: theme.palette.warning.main,
              color: theme.palette.warning.contrastMain,
              borderColor: "transparent",
            },
        //* Type : Error
        error: isHovered
          ? {
              backgroundColor: Color(theme.palette.error.main)
                .darken(BUTTON_SOLID_DARKEN)
                .hex(),
              color: theme.palette.error.contrastMain,
              borderColor: "transparent",
            }
          : {
              backgroundColor: theme.palette.error.main,
              color: theme.palette.error.contrastMain,
              borderColor: "transparent",
            },
      },
      //* Variant : Outlined
      outlined: {
        //* Type : Primary
        primary: isHovered
          ? {
              backgroundColor: Color(theme.palette.primary.light).hex(),
              color: theme.palette.primary.contrastLight,
              borderColor: Color(theme.palette.primary.contrastLight).darken(
                BUTTON_OUTLINED_DARKEN
              ),
            }
          : {
              backgroundColor: theme.palette.primary.light,
              color: theme.palette.primary.contrastLight,
              borderColor: Color(theme.palette.primary.contrastLight).lighten(
                BUTTON_OUTLINED_LIGHTEN
              ),
            },
        //* Type : Secondary
        secondary: isHovered
          ? {
              backgroundColor: Color(theme.palette.secondary.light).hex(),
              color: theme.palette.secondary.contrastLight,
              borderColor: Color(theme.palette.secondary.contrastLight).darken(
                BUTTON_OUTLINED_DARKEN
              ),
            }
          : {
              backgroundColor: theme.palette.secondary.light,
              color: theme.palette.secondary.contrastLight,
              borderColor: Color(theme.palette.secondary.contrastLight).lighten(
                BUTTON_OUTLINED_LIGHTEN
              ),
            },
        //* Type : Success
        success: isHovered
          ? {
              backgroundColor: Color(theme.palette.success.light).hex(),
              color: theme.palette.success.contrastLight,
              borderColor: Color(theme.palette.success.contrastLight).darken(
                BUTTON_OUTLINED_DARKEN
              ),
            }
          : {
              backgroundColor: theme.palette.success.light,
              color: theme.palette.success.contrastLight,
              borderColor: Color(theme.palette.success.contrastLight).lighten(
                BUTTON_OUTLINED_LIGHTEN
              ),
            },
        //* Type : Warning
        warning: isHovered
          ? {
              backgroundColor: Color(theme.palette.warning.light).hex(),
              color: theme.palette.warning.contrastLight,
              borderColor: Color(theme.palette.warning.contrastLight).darken(
                BUTTON_OUTLINED_DARKEN
              ),
            }
          : {
              backgroundColor: theme.palette.warning.light,
              color: theme.palette.warning.contrastLight,
              borderColor: Color(theme.palette.warning.contrastLight).lighten(
                BUTTON_OUTLINED_LIGHTEN
              ),
            },
        //* Type : Error
        error: isHovered
          ? {
              backgroundColor: Color(theme.palette.error.light).hex(),
              color: theme.palette.error.contrastLight,
              borderColor: Color(theme.palette.error.contrastLight).darken(
                BUTTON_OUTLINED_DARKEN
              ),
            }
          : {
              backgroundColor: theme.palette.error.light,
              color: theme.palette.error.contrastLight,
              borderColor: Color(theme.palette.error.contrastLight).lighten(
                BUTTON_OUTLINED_LIGHTEN
              ),
            },
      },
      //* Variant : Light
      light: {
        //* Type : Primary
        primary: isHovered
          ? {
              backgroundColor: Color(theme.palette.primary.light)
                .darken(BUTTON_LIGHT_DARKEN)
                .hex(),
              color: theme.palette.primary.contrastLight,
              borderColor: "transparent",
            }
          : {
              backgroundColor: theme.palette.primary.light,
              color: theme.palette.primary.contrastLight,
              borderColor: "transparent",
            },
        //* Type : Secondary
        secondary: isHovered
          ? {
              backgroundColor: Color(theme.palette.secondary.light)
                .darken(BUTTON_LIGHT_DARKEN)
                .hex(),
              color: theme.palette.secondary.contrastLight,
              borderColor: "transparent",
            }
          : {
              backgroundColor: theme.palette.secondary.light,
              color: theme.palette.secondary.contrastLight,
              borderColor: "transparent",
            },
        //* Type : Success
        success: isHovered
          ? {
              backgroundColor: Color(theme.palette.success.light)
                .darken(BUTTON_LIGHT_DARKEN)
                .hex(),
              color: theme.palette.success.contrastLight,
              borderColor: "transparent",
            }
          : {
              backgroundColor: theme.palette.success.light,
              color: theme.palette.success.contrastLight,
              borderColor: "transparent",
            },
        //* Type : Warning
        warning: isHovered
          ? {
              backgroundColor: Color(theme.palette.warning.light)
                .darken(BUTTON_LIGHT_DARKEN)
                .hex(),
              color: theme.palette.warning.contrastLight,
              borderColor: "transparent",
            }
          : {
              backgroundColor: theme.palette.warning.light,
              color: theme.palette.warning.contrastLight,
              borderColor: "transparent",
            },
        //* Type : Error
        error: isHovered
          ? {
              backgroundColor: Color(theme.palette.error.light)
                .darken(BUTTON_LIGHT_DARKEN)
                .hex(),
              color: theme.palette.error.contrastLight,
              borderColor: "transparent",
            }
          : {
              backgroundColor: theme.palette.error.light,
              color: theme.palette.error.contrastLight,
              borderColor: "transparent",
            },
      },
    };

    const varinatKey = buttonColorLookup[variant] ? variant : "solid";
    const typeKey = buttonColorLookup[varinatKey][type] ? type : "primary";

    return {
      backgroundColor: buttonColorLookup[varinatKey][typeKey].backgroundColor,
      borderColor: buttonColorLookup[varinatKey][typeKey].borderColor,
      color: buttonColorLookup[varinatKey][typeKey].color,
    };
  }

  /**
   * Returns the Button component's size styles.
   *
   * @param {String} size
   * @returns {Object} An object containing the Button component's size styles.
   */
  static getButtonSizeStyles(size) {
    // Size
    const buttonSizeLookup = {
      xs: { minWidth: "60px", height: "28px" },
      sm: { minWidth: "80px", height: "32px" },
      md: { minWidth: "100px", height: "36px" },
      lg: { minWidth: "120px", height: "40px" },
      xl: { minWidth: "240px", height: "56px" },
    };

    const DEFAULT_BUTTON_SIZE = "md";
    const BUTTON_SIZE = CommonUtils.getCSSPropertyValue(
      size,
      buttonSizeLookup,
      false,
      DEFAULT_BUTTON_SIZE
    );

    return {
      height: BUTTON_SIZE.height,
      minWidth: BUTTON_SIZE.minWidth,
    };
  }

  /**
   * Returns the Button component's spacing styles.
   *
   * @param {String} size
   * @returns {Object} An object containing the Button component's spacing styles.
   */
  static getButtonSpacingStyles(size) {
    // Padding
    const buttonPaddingLookup = {
      xs: "0px 8px",
      sm: "0px 12px",
      md: "0px 16px",
      lg: "0px 20px",
    };

    const DEFAULT_BUTTON_PADDING = "md";
    const BUTTON_PADDING = CommonUtils.getCSSPropertyValue(
      size,
      buttonPaddingLookup,
      false,
      DEFAULT_BUTTON_PADDING
    );

    return {
      padding: BUTTON_PADDING,
    };
  }
}

export default ButtonUtils;
